import { Claim, TenantCapabilities } from '../@types/organization';

export enum PermissionClaims {
  TenantCreate = 'tenant.c',
  TenantRead = 'tenant.r',
  TenantUpdate = 'tenant.u',
  TenantDelete = 'tenant.d',
  ChildTenantRead = 'child_tenant.r',
  ChildTenantUpdate = 'child_tenant.u',
  ChildTenantDelete = 'child_tenant.d',
  GlobalTokenUsageLogRead = 'gl_tul.r',
  UserCreate = 'user.c',
  UserRead = 'user.r',
  UserUpdate = 'user.u',
  UserDelete = 'user.d',
  ApiKeyCreate = 'apiKey.c',
  ApiKeyRead = 'apiKey.r',
  ApiKeyUpdate = 'apiKey.u',
  ApiKeyDelete = 'apiKey.d',
  TenantUserAccessCrud = 'tua.crud',
  SenderCreate = 'sender.c',
  SenderRead = 'sender.r',
  SenderUpdate = 'sender.u',
  SenderDelete = 'sender.d',
  OrderCreate = 'order.c',
  OrderRead = 'order.r',
  OrderUpdate = 'order.u',
  OrderDelete = 'order.d',
  OrderTypeManage = 'order_type.cru',
  AiSupportConfig = 'aisc.ru',
  AiSurveyResponseCreate = 'aisr.c',
  AiFeedbackResponseRead = 'aifr.r',
  FeatureFlagManage = 'feature_flag.ru',
  ProviderManage = 'provider.ru',
  ResponseUpdate = 'response.u',
  CampaignMemberUpdate = 'campaign_member.u',
  ReminderCreate = 'reminder.c',
  BrandedLinkCreate = 'bl.c',
  BrandedLinkUpdate = 'bl.u',
  BrandedLinkDelete = 'bl.d',
  TenantClaimsAssign = 'claim.t',
  GlobalClaimsAssign = 'claim.g'
}

export const PERMISSION_CLAIM_GUARDS: Record<PermissionClaims, PermissionClaims> = {
  [PermissionClaims.TenantCreate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.TenantRead]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.TenantUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.TenantDelete]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.UserCreate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.UserRead]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.UserUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.UserDelete]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ApiKeyCreate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ApiKeyRead]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ApiKeyUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ApiKeyDelete]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.TenantUserAccessCrud]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ResponseUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.CampaignMemberUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ReminderCreate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.BrandedLinkCreate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.BrandedLinkUpdate]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.BrandedLinkDelete]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.AiFeedbackResponseRead]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.TenantClaimsAssign]: PermissionClaims.TenantClaimsAssign,
  [PermissionClaims.ChildTenantRead]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.ChildTenantUpdate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.ChildTenantDelete]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.GlobalTokenUsageLogRead]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.SenderCreate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.SenderRead]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.SenderUpdate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.SenderDelete]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.OrderCreate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.OrderRead]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.OrderUpdate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.OrderDelete]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.OrderTypeManage]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.AiSupportConfig]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.AiSurveyResponseCreate]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.FeatureFlagManage]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.ProviderManage]: PermissionClaims.GlobalClaimsAssign,
  [PermissionClaims.GlobalClaimsAssign]: PermissionClaims.GlobalClaimsAssign
};

export const INTERNAL_PERMISSION_CLAIMS: ReadonlySet<string> = new Set<string>(
  Object.values(PermissionClaims).filter(
    (claimValue) => PERMISSION_CLAIM_GUARDS[claimValue] === PermissionClaims.GlobalClaimsAssign
  )
);

export const NON_ASSIGNABLE_PERMISSION_CLAIMS: ReadonlySet<string> = new Set<string>([
  PermissionClaims.GlobalClaimsAssign
]);

export const isPermissionClaimAvailable = (
  claimValue: string,
  capabilities: TenantCapabilities
): boolean => {
  if (capabilities.assignablePermissionClaims != null) {
    return capabilities.assignablePermissionClaims.includes(claimValue);
  }
  return claimValue !== PermissionClaims.AiFeedbackResponseRead || capabilities.isAiFeedbackEnabled;
};

/**
 * @deprecated Legacy claims. Do not add new entries — rework these into permission claim once we have orders.
 */
export const CLAIMS = {
  applications: {
    basic: 'applications_basic'
  },
  features: {
    pgap: 'features_pgap',
    ivr: 'features_ivr',
    review: 'features_review'
  },
  management: {
    etrack1: 'management_etrack1',
    surveyjs: 'management_surveyjs'
  },
  beta: 'beta'
};

export const MANAGEMENT_CLAIM_TYPES = ['features', 'management'];
export const UNTOGGLEABLE_CLAIM_VALUES = ['beta'];

type GetClaimsByTypeProps = {
  claimType: string;
  onlyIncludeToggleable?: boolean;
};

export const getClaimsByType = ({
  claimType,
  onlyIncludeToggleable
}: GetClaimsByTypeProps): Claim[] => {
  const newClaimsArray = [];

  for (const [currentClaimType, claimValues] of Object.entries(CLAIMS)) {
    if (currentClaimType === claimType.toLowerCase() && typeof claimValues !== 'string') {
      for (const claimValue of Object.keys(claimValues)) {
        if (!onlyIncludeToggleable || !UNTOGGLEABLE_CLAIM_VALUES.includes(claimValue)) {
          newClaimsArray.push({ claimType, claimValue });
        }
      }
    } else if (
      typeof claimValues === 'string' &&
      (!onlyIncludeToggleable || !UNTOGGLEABLE_CLAIM_VALUES.includes(claimValues))
    ) {
      newClaimsArray.push({ claimType, claimValue: claimValues });
    }
  }
  return newClaimsArray;
};
